import { useApolloClient } from '@apollo/client';
import { useModalV2 } from 'common/modal/hooks/useModalV2';
import { useCreateCandidateProfileListMutation } from 'generated-graphql/hooks/apollo-hooks';
import { SearchCandidateProfileListsDocument } from 'generated-graphql/graphql';
import { getTalentListForm } from 'modules/candidate-profile/utils/getTalentListForm';
import { useOrganization } from 'core/stores/hooks/useOrganization';
import { ToastType } from 'services/toast/types';
import { useToast } from 'services/toast/hooks';
import { waitForOpenSearchAndWeaviate } from 'modules/candidate-profile/utils/waitForOpenSearchAndWeaviate';

type UseCreateTalentPoolModalProps = {
  onSuccess: (talentPoolId: string) => void;
};

export const useCreateTalentPoolModal = ({
  onSuccess
}: UseCreateTalentPoolModalProps) => {
  const client = useApolloClient();
  const { openModal } = useModalV2();
  const { showToast } = useToast();
  const [createCandidateProfileList] = useCreateCandidateProfileListMutation();
  const organization = useOrganization();

  const openCreateTalentPoolModal = () => {
    const { schema, fields, defaultTeamIds, teamState } = getTalentListForm();

    openModal({
      width: '450px',
      title: 'Create talent list',
      primaryButtonText: 'Confirm',
      secondaryButtonText: 'Cancel',
      form: {
        schema,
        fields,
        defaultValues: { teamIds: defaultTeamIds }
      },
      onPrimaryAction: async (data) => {
        try {
          const { data: createData } = await createCandidateProfileList({
            variables: {
              input: {
                title: data.name,
                description: data.description,
                organizationId: organization.id,
                ...(teamState.sendsTeams && { teamIds: data.teamIds })
              }
            }
          });

          const newTalentPoolId =
            createData?.createCandidateProfileListForUser?.id;

          if (!newTalentPoolId) {
            showToast({
              title: 'Failed to create talent list',
              type: ToastType.Error
            });
            return;
          }

          await waitForOpenSearchAndWeaviate();

          // Refetch talent lists to show the new one
          await client.refetchQueries({
            include: [SearchCandidateProfileListsDocument]
          });

          showToast({
            title: 'Talent list created',
            type: ToastType.Success
          });

          onSuccess(newTalentPoolId);
        } catch (error) {
          showToast({
            title: 'Failed to create talent list',
            message: error instanceof Error ? error.message : undefined,
            type: ToastType.Error
          });
        }
      }
    });
  };

  return { openCreateTalentPoolModal };
};
